import { deriveAccent, hslToString, parseHslString, pickForeground, type Hsl } from './hsl-utils';
import {
  DEFAULT_PRESET_ID,
  THEME_PRESETS,
  type ColorMode,
  type Density,
  type ThemePalette,
} from './presets';
import type { SidebarStyle } from './store';

/**
 * Resolve which palette to use given preset id + mode (with system mode
 * resolved to the user's OS preference) + optional custom primary override.
 */
export function resolvePalette(
  presetId: string,
  mode: ColorMode,
  customPrimary: string | null,
): { palette: ThemePalette; isDark: boolean; primaryHsl: Hsl } {
  const preset = THEME_PRESETS.find((p) => p.id === presetId) ?? THEME_PRESETS[0];
  if (!preset) throw new Error('No theme presets defined');
  const isDark =
    mode === 'dark' ||
    (mode === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);
  let palette: ThemePalette = isDark ? preset.dark : preset.light;

  if (customPrimary) {
    const hsl = parseHslString(customPrimary);
    if (hsl) {
      const fg = pickForeground(hsl);
      const accent = deriveAccent(hsl, isDark);
      palette = {
        ...palette,
        primary: customPrimary,
        primaryForeground: fg,
        ring: customPrimary,
        accent: accent.bg,
        accentForeground: accent.fg,
      };
    }
  }

  const primaryHsl = parseHslString(palette.primary) ?? { h: 222, s: 47, l: 17 };
  return { palette, isDark, primaryHsl };
}

/**
 * Derive sidebar tokens (sidebar / -foreground / -muted / -accent / -border)
 * from the primary color + chosen sidebar style. This lets a single set of
 * presets render three very different sidebar treatments without keeping
 * separate palette branches.
 */
export function deriveSidebar(
  primary: Hsl,
  style: SidebarStyle,
  isDark: boolean,
): Pick<
  ThemePalette,
  'sidebar' | 'sidebarForeground' | 'sidebarMuted' | 'sidebarAccent' | 'sidebarBorder'
> {
  switch (style) {
    case 'bold': {
      // Deep dark sidebar with the brand hue tinting it.
      const l = isDark ? 6 : 12;
      return {
        sidebar: `${Math.round(primary.h)} 30% ${l}%`,
        sidebarForeground: '210 40% 98%',
        sidebarMuted: `${Math.round(primary.h)} 12% ${isDark ? 55 : 65}%`,
        sidebarAccent: `${Math.round(primary.h)} 30% ${isDark ? 14 : 20}%`,
        sidebarBorder: `${Math.round(primary.h)} 30% ${isDark ? 14 : 18}%`,
      };
    }
    case 'brand': {
      // Sidebar IS the brand color, mid-saturation so it stays readable.
      const sat = Math.min(primary.s, 55);
      const l = isDark ? 22 : 28;
      const sidebar: Hsl = { h: primary.h, s: sat, l };
      return {
        sidebar: hslToString(sidebar),
        sidebarForeground: pickForeground(sidebar),
        sidebarMuted: `${Math.round(primary.h)} ${Math.round(sat * 0.5)}% ${isDark ? 70 : 78}%`,
        sidebarAccent: `${Math.round(primary.h)} ${Math.round(sat)}% ${isDark ? 30 : 38}%`,
        sidebarBorder: `${Math.round(primary.h)} ${Math.round(sat)}% ${isDark ? 16 : 22}%`,
      };
    }
    case 'subtle': {
      // Light sidebar with subtle borders; brand color only on hover/active.
      if (isDark) {
        return {
          sidebar: '222 47% 8%',
          sidebarForeground: '210 40% 92%',
          sidebarMuted: '215 16% 55%',
          sidebarAccent: `${Math.round(primary.h)} ${Math.min(primary.s, 60)}% 18%`,
          sidebarBorder: '217 33% 14%',
        };
      }
      return {
        sidebar: '0 0% 100%',
        sidebarForeground: '222 47% 17%',
        sidebarMuted: '215 16% 47%',
        sidebarAccent: `${Math.round(primary.h)} ${Math.min(primary.s, 95)}% 95%`,
        sidebarBorder: '214 32% 91%',
      };
    }
  }
}

/**
 * Write the resolved palette + density + radius + sidebar style to
 * document.documentElement CSS variables. The whole design system reads
 * from these vars so all components update automatically.
 */
export function applyTheme(opts: {
  presetId: string;
  mode: ColorMode;
  density: Density;
  radius: string;
  customPrimary: string | null;
  sidebarStyle: SidebarStyle;
}): void {
  const root = document.documentElement;
  const { palette, isDark, primaryHsl } = resolvePalette(
    opts.presetId,
    opts.mode,
    opts.customPrimary,
  );
  const sidebarTokens = deriveSidebar(primaryHsl, opts.sidebarStyle, isDark);

  root.classList.toggle('dark', isDark);
  root.style.setProperty('--primary', palette.primary);
  root.style.setProperty('--primary-foreground', palette.primaryForeground);
  root.style.setProperty('--ring', palette.ring);
  root.style.setProperty('--accent', palette.accent);
  root.style.setProperty('--accent-foreground', palette.accentForeground);

  root.style.setProperty('--sidebar', sidebarTokens.sidebar);
  root.style.setProperty('--sidebar-foreground', sidebarTokens.sidebarForeground);
  root.style.setProperty('--sidebar-muted', sidebarTokens.sidebarMuted);
  root.style.setProperty('--sidebar-accent', sidebarTokens.sidebarAccent);
  root.style.setProperty('--sidebar-border', sidebarTokens.sidebarBorder);

  root.style.setProperty('--radius', opts.radius);
  root.dataset.density = opts.density;
}

export const INITIAL_THEME = {
  presetId: DEFAULT_PRESET_ID,
};
